import { useEffect } from "react";

import { useQuery } from "@tanstack/react-query";
import Hls from "hls.js";

import { getRecordingInfo } from "@app/analytics/api/meetingReport";
import { UnauthorizedVideoRecording, VideoRecording } from "@app/common/types";

export const hasVideoRecording = (
  videoRecording?: VideoRecording | UnauthorizedVideoRecording
): videoRecording is VideoRecording =>
  videoRecording != null &&
  videoRecording.status !== "unauthorized" &&
  videoRecording.playlistUrl != null &&
  videoRecording.recordingAccessToken != null;

export type UseRecordingInfoData = VideoRecording | UnauthorizedVideoRecording;

interface UseRecordingInfoOps {
  attemptOnUnsupportedBrowser?: boolean;
  enabled?: boolean;
  onSuccess?: (data: UseRecordingInfoData) => void;
}

const useRecordingInfo = (sessionId?: string, opts?: UseRecordingInfoOps) => {
  const {
    data: recordingInfo,
    isInitialLoading,
    ...query
  } = useQuery<UseRecordingInfoData>(
    // get the recording id and token from the BE
    {
      queryKey: ["recording", sessionId],
      queryFn: () => getRecordingInfo(sessionId!),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      retryOnMount: false,
      enabled:
        (opts?.enabled ?? true) &&
        Boolean(sessionId) &&
        (Boolean(opts?.attemptOnUnsupportedBrowser) || Hls.isSupported()),
    }
  );

  useEffect(() => {
    if (query.isSuccess && recordingInfo && opts?.onSuccess) {
      opts.onSuccess(recordingInfo);
    }
  }, [query.isSuccess, recordingInfo]);

  return {
    ...query,
    isInitialLoading,
    hasRecording: !isInitialLoading && hasVideoRecording(recordingInfo),
    recordingInfo,
  };
};

export default useRecordingInfo;
